import { useState } from "react";
import { Link } from "react-router-dom";
import { ImageIcon, Lock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cn, formatDate } from "@/lib/utils";
import { useImageTrialState } from "@/hooks/useImageTrial";
import type { ImageTrialStateView } from "@/types/imageTrial";

/**
 * Image trial progress banner — PROD-PRICING-I-2 (watermark-preview model).
 *
 * Renders globally above the app shell (sibling of `TrialBanner` in
 * `frontend/src/components/layout/Layout.tsx`). Visibility predicate
 * lives in `pickVariant()` below — the priority table from
 * docs/product/experience-and-ui.md § Cross-cutting § Starter Image Trial banner:
 *
 *  1. trial_ended  — terminal warning, non-dismissible
 *  2. day_6        — warning, non-dismissible
 *  3. day_3        — warning, dismissible
 *  4. early_trial  — info, dismissible (watermarked, no escalation day)
 *  5. null         — hidden
 *
 * The banner frames the WATERMARK, not a usage cap: trial generation is
 * unlimited; output is watermarked (`watermark_required`) until the
 * workspace converts. There is no cap-reached / cap-approaching variant.
 *
 * Dismissal uses sessionStorage keyed by the variant id; the next session
 * surfaces the banner again. Dismissal contract aligns with
 * `Cross-cutting § Dismissal contract` (7-day soft dismiss); the
 * session-scoped store is the V1 implementation.
 */

type Variant = "trial_ended" | "day_6" | "day_3" | "early_trial";

function pickVariant(state: ImageTrialStateView): Variant | null {
  // Suppression: non-Starter OR never-used image generation.
  if (state.tier !== "starter") return null;
  if (state.usage_count === 0) return null;

  // Terminal: the trial window closed without conversion.
  if (state.image_trial_expired_at !== null) {
    return "trial_ended";
  }

  // Active trial — escalate by day, else the neutral watermarked nudge.
  // Only show while output is actually watermarked (still trialing).
  if (!state.watermark_required) return null;

  if (state.current_trial_day === 6) return "day_6";
  if (state.current_trial_day === 3) return "day_3";
  if (state.current_trial_day !== null) return "early_trial";
  return null;
}

const NON_DISMISSIBLE: ReadonlySet<Variant> = new Set(["trial_ended", "day_6"]);

const TERMINAL: ReadonlySet<Variant> = new Set(["trial_ended"]);

function dismissalKey(variant: Variant): string {
  return `aiaso:dismiss:image-trial:${variant}`;
}

function isDismissed(variant: Variant): boolean {
  if (NON_DISMISSIBLE.has(variant)) return false;
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(dismissalKey(variant)) === "1";
}

function buildCopy(
  state: ImageTrialStateView,
  variant: Variant,
  t: TFunction,
): string {
  const endDate = formatDate(state.trial_end_at);
  switch (variant) {
    case "trial_ended":
      // PINNED (pins.json → verify_starter_image_trial.py) — this exact
      // sentence MUST stay inline verbatim; never extract to i18n.
      return "Your 7-day Starter trial has ended. Upgrade to keep generating images, or continue with text-only experiments while ASOLOOP collects more signal.";
    case "day_6":
      return t("billing.imageTrialBanner.day6Copy", { endDate });
    case "day_3":
      return t("billing.imageTrialBanner.day3Copy", { endDate });
    case "early_trial":
      return t("billing.imageTrialBanner.earlyTrialCopy", { endDate });
  }
}

interface TierTokens {
  bg: string;
  border: string;
  text: string;
  ctaBorder: string;
  ctaText: string;
  ctaHover: string;
  ctaRing: string;
  icon: typeof ImageIcon;
}

function tokensFor(variant: Variant): TierTokens {
  if (variant === "early_trial") {
    return {
      bg: "bg-primary-50",
      border: "border-primary-100",
      text: "text-primary-900",
      ctaBorder: "border-primary-200",
      ctaText: "text-primary-900",
      ctaHover: "hover:bg-primary-100",
      ctaRing: "focus-visible:ring-primary-500",
      icon: ImageIcon,
    };
  }
  return {
    bg: "bg-amber-50",
    border: "border-amber-300",
    text: "text-amber-900",
    ctaBorder: "border-amber-300",
    ctaText: "text-amber-900",
    ctaHover: "hover:bg-amber-100",
    ctaRing: "focus-visible:ring-amber-500",
    icon: TERMINAL.has(variant) ? Lock : ImageIcon,
  };
}

export default function ImageTrialBanner() {
  const { t } = useTranslation();
  const [dismissTick, setDismissTick] = useState(0);
  const { data } = useImageTrialState();

  if (!data) return null;
  const variant = pickVariant(data);
  if (!variant) return null;
  if (isDismissed(variant)) return null;
  // Force re-evaluation of dismissal state when the user dismisses.
  void dismissTick;

  const copy = buildCopy(data, variant, t);
  const tokens = tokensFor(variant);
  const Icon = tokens.icon;
  const dismissible = !NON_DISMISSIBLE.has(variant);
  // Cache the variant for the closure below — TypeScript control-flow
  // analysis does not carry the `if (!variant) return null` narrowing
  // into nested closures.
  const variantForDismiss = variant;

  function handleDismiss() {
    window.sessionStorage.setItem(dismissalKey(variantForDismiss), "1");
    setDismissTick((tick) => tick + 1);
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-image-trial-variant={variant}
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2 text-sm",
        tokens.bg,
        tokens.border,
        tokens.text,
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <Icon size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p>{copy}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to="/settings/billing"
          className={cn(
            "rounded-md border px-3 py-1 text-xs font-semibold",
            tokens.ctaBorder,
            tokens.ctaText,
            tokens.ctaHover,
            "focus-visible:outline-none focus-visible:ring-2",
            tokens.ctaRing,
          )}
        >
          {variant === "early_trial"
            ? t("billing.imageTrialBanner.managePlanButton")
            : t("billing.imageTrialBanner.upgradeButton")}
        </Link>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t("billing.imageTrialBanner.dismissAriaLabel")}
            className={cn(
              "rounded-md p-1",
              tokens.ctaHover,
              "focus-visible:outline-none focus-visible:ring-2",
              tokens.ctaRing,
            )}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
